import { useMemo } from "react";
import moment from "moment-timezone";
import { ProgramPhase } from "@core/features/program";
import type { Article } from "@core/features/support";
import { ACTION_TYPE_SLUG } from "@core/features/actions";
import { getUserMeetingRoleFromMeeting } from "@platform/features/programPhases/utils";
import { usePlatformStore, useSupportSheet } from "@platform/store";
import { ProgramPhaseItem } from "./ProgramPhaseItem";
import { useDialog } from "@platform/store";
import { useProgram } from "@platform/hooks/useProgram";
import type { Meeting } from "@platform/types/platform.types";

type Props = {
  phase: ProgramPhase;
  isLoading: boolean;
};

export function ProgramPhaseItemContainer({ phase, isLoading }: Props) {
  const { program } = useProgram();
  const user = usePlatformStore((s) => s.user);
  const { open: openSupportSheet } = useSupportSheet();

  const { openDialog } = useDialog();

  const meetingAction = phase.actions?.find(
    (action) => action.action_type?.slug === ACTION_TYPE_SLUG.MEETING
  );

  // Cast binding_object_data to Meeting type for role lookup
  const meeting = meetingAction?.binding_object_data as
    | Meeting
    | null
    | undefined;

  const role = meeting
    ? getUserMeetingRoleFromMeeting(meeting, user?.id || null)
    : null;

  const isPhaseInScheduledEngagement = useMemo(() => {
    return (
      program?.scheduled_engagement_start_date &&
      program?.scheduled_engagement_end_date &&
      moment(phase.start_date).isSameOrAfter(
        program.scheduled_engagement_start_date
      ) &&
      moment(phase.end_date).isSameOrBefore(
        program.scheduled_engagement_end_date
      )
    );
  }, [
    program?.scheduled_engagement_start_date,
    program?.scheduled_engagement_end_date,
    phase.start_date,
    phase.end_date,
  ]);

  const handleMeetingInformationClick = () => {
    openDialog("meetingInformation", {
      meetingId: meeting?.id,
    });
  };

  const handleOpenArticle = (article: Article) => {
    openSupportSheet(article.slug);
  };

  return (
    <ProgramPhaseItem
      phase={phase}
      isLoading={isLoading}
      role={role}
      isPhaseInScheduledEngagement={isPhaseInScheduledEngagement || false}
      hasMeeting={!!meeting?.id}
      onMeetingInformationClick={handleMeetingInformationClick}
      onOpenArticle={handleOpenArticle}
    />
  );
}

export default ProgramPhaseItemContainer;

